import type { RefObject } from 'react';
import type { CatalogRoute } from './useCatalogRoute';

import { useRef, useEffect } from 'react';

import { clampPage } from 'src/utils/catalogUrl';

/**
 * Keeps the page of a catalog view honest once the api has said how many cars there are.
 *
 * - An address past the last page — a link to page 3 of a list that has since shrunk — is replaced
 *   by the last page, in place (`replace`), so Back does not return to the empty page.
 * - A new page starts at the top of the list: the page links sit under the grid, and the scroll
 *   container (not the document — `#root` never scrolls) would otherwise leave the person at the
 *   bottom of the next page, looking at the links they just pressed.
 *
 * `settledTotal` is the `total` of an answer for THIS view — `undefined` while the previous page is
 * still on screen as a placeholder, whose total may belong to another filter.
 */
export const useCatalogPaging = (
  route: Pick<CatalogRoute, 'page' | 'navigateTo'>,
  settledTotal: number | undefined,
  scrollContainer: RefObject<HTMLElement | null>
): void => {
  const { page, navigateTo } = route;

  useEffect(() => {
    if (settledTotal === undefined) return;

    const inRange = clampPage(page, settledTotal);

    if (inRange !== page) navigateTo({ page: inRange }, { replace: true });
  }, [settledTotal, page, navigateTo]);

  const shownPage = useRef(page);

  useEffect(() => {
    if (shownPage.current === page) return;

    shownPage.current = page;
    scrollContainer.current?.scrollTo({ top: 0 });
  }, [page, scrollContainer]);
};
